import { PriceCalendarFiltersState, PriceCalendarMonthlyState } from '@model/state/price-calendar-state';
import { Monthly, Offer } from '@model/iceberg/service/price-calendar/monthly-response';
import { FromPricePayload, PriceCalendarApi } from '@model/iceberg/service/price-calendar/price-calendar-api';
import { PriceCalendarMonthlyActions } from '../priceCalendarActions';
import moment from 'moment';
import { ISO_8601_DATE_FORMAT, MONTH_NUMBER, YEAR_MONTH_FORMAT, YEAR_NUMBER } from '@model/common';
import { DOB, Occupancy } from '@model/state';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DealFinderResultsActions, setFilters } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { RootState } from '@state/store';
import { hasChildrenWithErrors } from '@model/search';
import { priceCalendarFilterActions } from '@state/slices/price-calendar/filter/priceCalendarFilterSlice';

export const INITIAL_PRICE_CALENDAR_MONTHLY_STATE: PriceCalendarMonthlyState = {
  response: {
    data: {
      prices: [],
      query: {
        duration: 0,
        month: 0,
        year: 0,
        path: '',
        boardBasis: '',
        from: '',
        occupancy: []
      }
    },
    loading: false,
    error: null
  },
  date: '',
  defaultDate: '',
  month: ''
};

export const requestMonthly = createAsyncThunk<Monthly, FromPricePayload, { state: RootState; rejectValue: any }>(
  PriceCalendarMonthlyActions.FETCH_MONTHLY_PRICES,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    dispatch(setLoading(true));
    const state = getState() as RootState;
    try {
      const response = await new PriceCalendarApi().monthly(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    } finally {
      if (state.priceCalendar.monthly.prices) {
        dispatch(setLoading(false));
      }
    }
  }
);

export const getOccupancy = (occupancy: Array<Occupancy>) => {
  return (occupancy || []).map(({ adults, children }: Occupancy) => ({
    adults,
    children: children.map((dob: DOB) =>
      moment(`${dob.year}-${dob.month}-${dob.day}`, ISO_8601_DATE_FORMAT).format(ISO_8601_DATE_FORMAT)
    )
  }));
};

export const getDefaultDate = (offers: Array<Offer> = []): string => {
  if (!offers.length) return '';
  const cheapest: Offer | undefined = offers.find((offer: Offer) => offer.cheapest);
  const cheapestDate: string = cheapest ? cheapest.date : offers[0].date;
  return moment(cheapestDate).format(ISO_8601_DATE_FORMAT);
};

export const handleOnFetchFiltersFulfilled = createAsyncThunk(
  PriceCalendarMonthlyActions.FETCH_PRICES_ON_FETCH_FILTERS_FULFILLED,
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;

    const path = state.hotel.path;
    const hotelOnly = state.search.hotelOnly;
    const filters: PriceCalendarFiltersState = state.priceCalendar.filters;
    const { duration, airport: from, board: boardBasis, occupancy } = filters;
    const { month, year } = filters.response.data.defaults;
    const today = moment();
    const isValid: boolean = !hasChildrenWithErrors(occupancy);

    if (isValid) {
      await dispatch(
        requestMonthly({
          path,
          duration,
          from: hotelOnly ? '' : from,
          boardBasis,
          occupancy: getOccupancy(occupancy),
          month: month || (today.month() + 1).toString(),
          year: year || today.year().toString()
        })
      );
    }
  }
);

export const setMonth = createAsyncThunk(
  PriceCalendarMonthlyActions.SET_MONTH,
  async (payload: string, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const path = state.hotel.path;
      const filters: PriceCalendarFiltersState = state.priceCalendar.filters;
      const { duration, board: boardBasis, airport: from, occupancy } = filters;
      const date = moment(payload);

      await dispatch(
        requestMonthly({
          path,
          duration,
          from,
          boardBasis,
          occupancy: getOccupancy(occupancy),
          month: date.format(MONTH_NUMBER),
          year: date.format(YEAR_NUMBER)
        })
      );

      return payload;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const priceCalendarMonthlySlice = createSlice({
  name: PriceCalendarMonthlyActions.SLICE_NAME,
  initialState: INITIAL_PRICE_CALENDAR_MONTHLY_STATE,
  reducers: {
    clearMonthly: () => INITIAL_PRICE_CALENDAR_MONTHLY_STATE,
    setDay: (state, action: PayloadAction<string>) => {
      state.date = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.response.loading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestMonthly.pending, (state) => {
        state.response.loading = true;
        state.response.error = null;
      })
      .addCase(requestMonthly.fulfilled, (state, action) => {
        const monthly: Monthly = action.payload;
        const {
          query: { month: queryMonth, year }
        } = monthly;
        state.response.data = monthly;
        state.response.loading = false;
        state.response.error = null;
        state.defaultDate = getDefaultDate(monthly.prices);
        state.month = moment()
          .year(year)
          .month(queryMonth - 1)
          .format(YEAR_MONTH_FORMAT);
      })
      .addCase(requestMonthly.rejected, (state, action) => {
        state.response.loading = false;
        state.response.error = action.error || null;
      })
      .addCase(setMonth.pending, (state) => {
        state.response.loading = true;
        state.response.error = null;
      })
      .addCase(setMonth.fulfilled, (state, action) => {
        state.response.loading = false;
        state.month = action.payload;
      })
      .addCase(setMonth.rejected, (state, action) => {
        state.response.loading = false;
        state.response.error = (action.payload as string) || null;
      })
      .addCase(priceCalendarFilterActions.fetchFilters.pending, (state, action: any) => {
        if (!action.meta.arg) {
          return INITIAL_PRICE_CALENDAR_MONTHLY_STATE;
        }
        const { path, urlParams } = action.meta.arg;
        const { from, boardBasis, duration, occupancy, year, month, day } = urlParams;
        const dayOfMonth = state.date ? moment(state.date).date() : day;

        let responseDate: string = '';
        let responseMonth: string = '';

        if (month && year && dayOfMonth) {
          const daysInMonth = moment()
            .year(year)
            .month(month - 1)
            .daysInMonth();
          const momentDate = moment()
            .year(year)
            .month(month - 1)
            .date(Math.min(dayOfMonth, daysInMonth));
          responseDate = momentDate.format(ISO_8601_DATE_FORMAT);
          responseMonth = momentDate.format(YEAR_MONTH_FORMAT);
        }

        return {
          ...state,
          response: {
            data: {
              prices: [],
              query: {
                duration,
                month,
                year,
                path,
                boardBasis,
                from,
                occupancy
              }
            },
            loading: false,
            error: null
          },
          month: responseMonth,
          date: state.date || responseDate
        };
      })
      .addCase(priceCalendarFilterActions.fetchFilters.rejected, (state) => {
        state.response = { ...INITIAL_PRICE_CALENDAR_MONTHLY_STATE.response };
      })
      .addCase(DealFinderResultsActions.CLEAR_RESULTS, (state: PriceCalendarMonthlyState) => state)
      .addCase(setFilters.fulfilled, (state: PriceCalendarMonthlyState) => state);
  }
});

export const { clearMonthly, setDay, setLoading } = priceCalendarMonthlySlice.actions;
export const priceCalendarMonthlyActions = {
  requestMonthly,
  setMonth,
  getOccupancy,
  getDefaultDate,
  handleOnFetchFiltersFulfilled
};
export const priceCalendarMonthlyReducer = priceCalendarMonthlySlice.reducer;
